import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './DistributionPage.css';
import { API_BASE_URL } from '../config';

const INDEX_OPTIONS = [
  { label: 'NIFTY', value: 'NIFTY' },
  { label: 'BANKNIFTY', value: 'BANKNIFTY' },
  { label: 'FINNIFTY', value: 'FINNIFTY' },
  { label: 'MIDCPNIFTY', value: 'MIDCPNIFTY' },
];

const DistributionPage = ({ onBack }) => {
  const [indexVal, setIndexVal] = useState('NIFTY');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pcrData, setPcrData] = useState(null); // { total_call_oi, total_put_oi, pcr, expiry_date }

  const toNumber = useCallback((value) => {
    if (value == null) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const num = parseFloat(value.replace(/,/g, ''));
      return Number.isFinite(num) ? num : 0;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }, []);

  const fetchPCR = useCallback(async (idx) => {
    setLoading(true);
    setError('');
    try {
      const url = `${API_BASE_URL}/api/pcr?index=${encodeURIComponent(idx)}`;
      const response = await fetch(url);
      const json = await response.json();
      if (!json.success) throw new Error(json.message || 'Request failed');
      const payload = json.data || {};
      setPcrData({
        ...payload,
        total_call_oi: toNumber(json.total_call_oi_lots ?? payload.total_call_oi_lots ?? payload.total_call_oi),
        total_put_oi: toNumber(json.total_put_oi_lots ?? payload.total_put_oi_lots ?? payload.total_put_oi),
        pcr: json.pcr_oi_change ?? payload.pcr_oi_change ?? payload.pcr_lots ?? payload.pcr,
      });
    } catch (e) {
      setPcrData(null);
      setError(e.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  }, [toNumber]);

  useEffect(() => {
    fetchPCR(indexVal);
  }, [indexVal, fetchPCR]);

  // Build UI-friendly dataset from API
  const currentData = useMemo(() => {
    if (!pcrData) return [];
    const { total_call_oi, total_put_oi } = pcrData;
    const total = total_call_oi + total_put_oi || 1;
    return [
      {
        name: 'Calls OI',
        allocationPct: +(total_call_oi / total * 100).toFixed(1),
        amount: total_call_oi,
        color: '#6366f1',
      },
      {
        name: 'Puts OI',
        allocationPct: +(total_put_oi / total * 100).toFixed(1),
        amount: total_put_oi,
        color: '#10b981',
      },
    ];
  }, [pcrData]);

  // Donut segments for SVG
  const donutPaths = useMemo(() => {
    const center = 150;
    const r = 80;
    let startAngle = 0;
    const paths = [];

    currentData.forEach((item) => {
      const angle = (item.allocationPct / 100) * 360;
      const endAngle = startAngle + angle;

      const x1 = center + r * Math.cos((startAngle - 90) * Math.PI / 180);
      const y1 = center + r * Math.sin((startAngle - 90) * Math.PI / 180);
      const x2 = center + r * Math.cos((endAngle - 90) * Math.PI / 180);
      const y2 = center + r * Math.sin((endAngle - 90) * Math.PI / 180);
      const largeArcFlag = angle > 180 ? 1 : 0;

      paths.push({
        d: `M ${center} ${center} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`,
        color: item.color,
      });

      startAngle = endAngle;
    });

    return paths;
  }, [currentData]);

  const centerTopText = useMemo(() => {
    if (!currentData.length) return '—';
    const calls = currentData.find((x) => x.name === 'Calls OI')?.allocationPct ?? 0;
    return `Calls ${Number(calls).toFixed(1)}%`;
  }, [currentData]);

  const centerBottomText = useMemo(() => {
    if (!currentData.length) return '—';
    const puts = currentData.find((x) => x.name === 'Puts OI')?.allocationPct ?? 0;
    return `Puts ${Number(puts).toFixed(1)}%`;
  }, [currentData]);

  return (
    <div className="distribution-page">
      <header className="distribution-header">
        <div className="distribution-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>

          <div className="distribution-search">
            <select
              value={indexVal}
              onChange={(e) => setIndexVal(e.target.value)}
              className="index-select"
              title="Select Index"
            >
              {INDEX_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <button
              className="distribution-refresh-btn"
              onClick={() => fetchPCR(indexVal)}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          <div className="distribution-user">
          </div>
        </div>
      </header>

      <main className="distribution-main">
        <div className="distribution-container">
          <div className="distribution-title s4u-title-row">
            <div className="s4u-page-heading">
              <button className="distribution-back-btn s4u-back-btn" onClick={onBack}>
                ← Back to Dashboard
              </button>
              <h1>PCR Distribution ({indexVal})</h1>
            </div>
            <div className="distribution-info">
              <span className="info-icon">📊</span>
              <span className="info-text">Live Calls vs Puts OI</span>
            </div>
          </div>

          <div className="distribution-chart">
            <div className="chart-container">
              <h3>Open Interest Distribution</h3>
              <div className="donut-chart">
                <svg width="180" height="180" viewBox="0 0 300 300" role="img" aria-label="PCR donut">
                  <circle cx="150" cy="150" r="80" fill="none" stroke="#1a1a1a" strokeWidth="60"/>
                  {donutPaths.map((seg, i) => (
                    <path key={i} d={seg.d} fill={seg.color} stroke="#0a0a0a" strokeWidth="2" />
                  ))}
                </svg>
                <div className="chart-center">
                  <div className="total-value">{centerTopText}</div>
                  <div className="total-label">{centerBottomText}</div>
                </div>
              </div>
              <div className="pcr-below">
                PCR: {pcrData?.pcr ?? (loading ? 'Loading…' : '—')}
              </div>
              {error && <p className="error-text">{error}</p>}
            </div>
          </div>

          <div className="distribution-table-container">
            <div className="table-header-info">
              <span className="results-count">Showing {currentData.length} categories</span>
              <span className="last-updated">Live from API</span>
            </div>

            <div className="distribution-table">
              <div className="table-row table-header-row">
                <div className="table-cell">Type</div>
                <div className="table-cell">Allocation %</div>
                <div className="table-cell">OI</div>
                <div className="table-cell">Visualization</div>
              </div>

              {currentData.map((item, index) => (
                <div key={index} className="table-row distribution-row">
                  <div className="table-cell category-name">
                    <div className="category-info">
                      <div className="color-indicator" style={{ backgroundColor: item.color }}></div>
                      <span className="category-title">{item.name}</span>
                    </div>
                  </div>
                  <div className="table-cell allocation-percent">
                    <span className="percent-value">{item.allocationPct.toFixed(1)}%</span>
                  </div>
                  <div className="table-cell allocation-amount">
                    <span className="amount-value">{item.amount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="table-cell allocation-bar">
                    <div className="bar-container">
                      <div
                        className="bar-fill"
                        style={{ width: `${item.allocationPct}%`, backgroundColor: item.color }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {pcrData && (
              <div className="note">
                <span className="meta">
                  Expiry: {pcrData.expiry_date} • Calls: {pcrData.total_call_oi.toLocaleString('en-IN')} • Puts: {pcrData.total_put_oi.toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DistributionPage;
